import cn from 'classnames'
import BaseFlyout from 'ext-ui/components/BaseFlyout/BaseFlyout'
import Button from 'ext-ui/components/Button/Button'
import Checkbox from 'ext-ui/components/Checkbox/Checkbox'
import TextAreaInput from 'ext-ui/components/TextAreaInput/TextAreaInput'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import {
  CountryCode,
  getCountryCallingCode,
  isValidPhoneNumber,
} from 'libphonenumber-js'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import CountryPhoneDropdown from 'src/components/CountryPhoneDropdown/CountryPhoneDropdown'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import PhoneTextInputField from 'src/components/PhoneTextInputField/PhoneTextInputField'
import MtAccountCollection from 'src/feature/MtAccountCollection/MtAccountCollection'
import { useTranslate } from 'src/localization/Trns'
import type { AccountCollectionFlowCompletionResponse } from 'src/queries/payments/payments.types'
import {
  useCompleteVendorAccountCollectionFlow,
  useConfirmVendorAccountCollectionFlow,
  useCreateVendor,
  useCreateVendorAccountCollectionFlow,
  useVendorDetail,
} from 'src/queries/vendors/vendors.queries'
import {
  ConfirmVendorAccountCollectionFlowInput,
  CreateVendorRequest,
  Vendor,
  VendorAccountCollectionFlowResponse,
  VendorStatus,
} from 'src/queries/vendors/vendors.types'

import style, {
  ackBlock,
  ackDescription,
  ackPrimary,
  ackRow,
  ackText,
  confirmationSurface,
  contactNameLabelOrder,
  description,
  fields,
  flyoutTitle,
  footer,
  footerActionsEnd,
  footerButtons,
  footerError,
  form,
  header,
  pendingNote,
  phoneField,
  phoneInput,
  phoneRow,
  trustBox,
} from './AddVendorFlyout.style'
import useVendorAccountConfirmation from './useVendorAccountConfirmation'
import {
  clearRecoverableVendor,
  getRecoverableVendorId,
  rememberRecoverableVendor,
} from './vendorAccountCollectionRecovery'
import { createVendorIdempotencyKey } from './vendorIdempotency'

export interface AddVendorFlyoutProps {
  isOpen: boolean
  onClose: () => void
  organizationId: string
  className?: string
}

/** Two steps, one flyout instance (prototype's `mvpStep`): the details form,
 * then the ACH payment step. Run 2 ships Step 1 + a Step 2 placeholder. */
type Step = 'details' | 'payment'

type PaymentStepPhase =
  | 'collecting'
  | 'acknowledgement'
  | 'confirming'
  | 'confirmation-pending'
  | 'confirmation-error'

type SubmittedVendorAccountCollectionFlow = Pick<
  VendorAccountCollectionFlowResponse,
  'flowId' | 'acknowledgementCopyVersion'
>

export const VENDOR_ACKNOWLEDGEMENT = {
  version: '2026-07-28',
  heading: 'I acknowledge that...',
  description:
    'These ACH account details are accurate and authorized for use when paying this vendor.',
} as const

/**
 * Reuse the app's established country picker + country-scoped phone input
 * pairing. The picker defaults to US but remains independent from the ACH rail:
 * it describes the vendor contact's phone number, not their bank account.
 */
const DEFAULT_PHONE_ISO_COUNTRY = 'US'

/** Anchored format check (the shared EMAIL_REGEX is unanchored and matches on
 * substrings). Presence is gated separately. */
const isValidEmailFormat = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())

const trimmedOrUndefined = (value: string): string | undefined => {
  const trimmed = value.trim()
  return trimmed === '' ? undefined : trimmed
}

/**
 * Add-Vendor flyout (Bill Pay M2). Routing-free: it owns no URL state — the
 * page owns open/close. Step 1 creates the vendor's Modern Treasury
 * Counterparty (`useCreateVendor`) and advances to Step 2; run 3 replaces the
 * Step 2 placeholder with the real ACH account-collection embed.
 *
 * Chassis: BaseFlyout (focus trap / Escape / overlay dismiss / focus
 * restoration to the triggering CTA are provided by its react-modal) + an
 * in-`children` header/footer, because BaseFlyout has no footer or stepper slot
 * — the AssignAccountOwnerFlyout pattern (DS gap flagged in the PR).
 */
const AddVendorFlyout: React.FC<AddVendorFlyoutProps> = ({
  isOpen,
  onClose,
  organizationId,
  className,
}) => {
  const translate = useTranslate()
  const recoverableVendorId = getRecoverableVendorId(organizationId)

  const [step, setStep] = useState<Step>(
    recoverableVendorId ? 'payment' : 'details',
  )
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [externalId, setExternalId] = useState('')
  const [notes, setNotes] = useState('')
  const [contactName, setContactName] = useState('')
  const [phone, setPhone] = useState('')
  const [phoneIsoCountry, setPhoneIsoCountry] = useState(
    DEFAULT_PHONE_ISO_COUNTRY,
  )
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [nameErrorArmed, setNameErrorArmed] = useState(false)
  const [emailErrorKind, setEmailErrorKind] = useState<
    'required' | 'invalid' | null
  >(null)
  const [phoneErrorArmed, setPhoneErrorArmed] = useState(false)

  // Step 2 state.
  const [createdVendor, setCreatedVendor] = useState<Vendor | null>(null)
  const [submittedFlow, setSubmittedFlow] =
    useState<SubmittedVendorAccountCollectionFlow | null>(null)
  const [acknowledged, setAcknowledged] = useState(false)
  const [
    hasUnsupportedAcknowledgementCopy,
    setHasUnsupportedAcknowledgementCopy,
  ] = useState(false)
  const createIdempotencyRef = useRef<{
    fingerprint: string
    key: string
  } | null>(null)
  const activeFlowRef = useRef<VendorAccountCollectionFlowResponse | null>(
    null,
  )
  const completionStatusRef = useRef<
    'PENDING' | 'AWAITING_ACKNOWLEDGEMENT' | 'CONFIRMED' | null
  >(null)
  const recoveryAttemptedFlowIdRef = useRef<string | null>(null)
  const isOpenRef = useRef(isOpen)
  isOpenRef.current = isOpen
  // Phase A <-> Phase B replaces the focused subtree (the MT embed unmounts),
  // which would drop focus to <body>. Move it to the region that appears
  // instead. tabIndex={-1}: programmatically focusable, not in the tab order.
  const confirmationRegionRef = useRef<HTMLDivElement | null>(null)
  const collectRegionRef = useRef<HTMLDivElement | null>(null)
  const dispatch = useDispatch()

  const clearVendorSession = useCallback((): void => {
    setStep('details')
    setName('')
    setEmail('')
    setExternalId('')
    setNotes('')
    setContactName('')
    setPhone('')
    setPhoneIsoCountry(DEFAULT_PHONE_ISO_COUNTRY)
    setSubmitError(null)
    setNameErrorArmed(false)
    setEmailErrorKind(null)
    setPhoneErrorArmed(false)
    setCreatedVendor(null)
    setSubmittedFlow(null)
    setAcknowledged(false)
    setHasUnsupportedAcknowledgementCopy(false)
    createIdempotencyRef.current = null
    activeFlowRef.current = null
    completionStatusRef.current = null
    recoveryAttemptedFlowIdRef.current = null
  }, [])

  const createVendor = useCreateVendor(organizationId, {
    // Same flyout instance advances to Step 2 (the new vendor is now in the
    // list as ACTION_REQUIRED via the hook's list invalidation). Keep the vendor
    // so Step 2 can bind its account-collection flow to the id.
    onSuccess: (vendor, variables) => {
      if (createIdempotencyRef.current?.key !== variables.idempotencyKey) {
        return
      }
      createIdempotencyRef.current = null
      rememberRecoverableVendor(organizationId, vendor.id)
      setCreatedVendor(vendor)
      setStep('payment')
    },
  })

  const createFlowMutation = useCreateVendorAccountCollectionFlow()
  const completeFlowMutation =
    useCompleteVendorAccountCollectionFlow(organizationId)
  const confirmFlowMutation =
    useConfirmVendorAccountCollectionFlow(organizationId)
  const recoveryVendorQuery = useVendorDetail(
    organizationId,
    isOpen && !createdVendor
      ? (recoverableVendorId ?? undefined)
      : undefined,
  )
  const confirmation = useVendorAccountConfirmation({
    confirmFlow: confirmFlowMutation.mutateAsync,
    onConfirmed: () => {
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: (
            <span>
              <strong>{translate('Vendor added')}</strong>{' '}
              {translate(
                '${vendorName} is now active with ACH payment details on file.',
                { vendorName: createdVendor?.name ?? '' },
              )}
            </span>
          ),
        }),
      )
      clearRecoverableVendor(
        organizationId,
        createdVendor?.id,
      )
      clearVendorSession()
      onClose()
    },
  })
  const resetConfirmation = confirmation.reset
  const paymentStepPhase: PaymentStepPhase =
    hasUnsupportedAcknowledgementCopy || confirmation.phase === 'error'
      ? 'confirmation-error'
      : confirmation.phase === 'confirming'
        ? 'confirming'
        : confirmation.phase === 'pending'
          ? 'confirmation-pending'
          : submittedFlow
            ? 'acknowledgement'
            : 'collecting'

  const handleCollectionSubmitted = useCallback(
    (flow: SubmittedVendorAccountCollectionFlow): void => {
      setAcknowledged(false)
      resetConfirmation()
      if (flow.acknowledgementCopyVersion !== VENDOR_ACKNOWLEDGEMENT.version) {
        setSubmittedFlow(null)
        setHasUnsupportedAcknowledgementCopy(true)
        return
      }
      setHasUnsupportedAcknowledgementCopy(false)
      setSubmittedFlow(flow)
    },
    [resetConfirmation],
  )
  const createFlowForEmbed = useCallback(async () => {
    const flow = await createFlowMutation.mutateAsync(createdVendor?.id ?? '')
    activeFlowRef.current = flow
    return flow
  }, [createFlowMutation, createdVendor])
  const completeFlowForEmbed = useCallback(
    async (flowId: string): Promise<AccountCollectionFlowCompletionResponse> => {
      const result = await completeFlowMutation.mutateAsync({
        vendorId: createdVendor?.id ?? '',
        flowId,
      })
      completionStatusRef.current = result.status
      return {
        status: result.status === 'PENDING' ? 'PENDING' : 'COMPLETED',
      }
    },
    [completeFlowMutation, createdVendor],
  )
  const handleCollectionLinked = useCallback((): void => {
    if (completionStatusRef.current === 'CONFIRMED') {
      clearRecoverableVendor(organizationId, createdVendor?.id)
      clearVendorSession()
      onClose()
      return
    }
    const flow = activeFlowRef.current
    if (flow) handleCollectionSubmitted(flow)
  }, [
    clearVendorSession,
    createdVendor,
    handleCollectionSubmitted,
    onClose,
    organizationId,
  ])

  useEffect(() => {
    if (!isOpen || createdVendor || !recoveryVendorQuery.data) return
    const vendor = recoveryVendorQuery.data
    setStep('payment')

    if (
      vendor.status === VendorStatus.Active ||
      vendor.paymentMethod?.status === 'PAYABLE'
    ) {
      clearRecoverableVendor(organizationId, vendor.id)
      clearVendorSession()
      onClose()
      return
    }

    const flow = vendor.accountCollectionFlow
    if (!flow) {
      setCreatedVendor(vendor)
      setSubmittedFlow(null)
      return
    }
    if (flow.status === 'AWAITING_ACKNOWLEDGEMENT') {
      setCreatedVendor(vendor)
      handleCollectionSubmitted(flow)
      return
    }
    if (recoveryAttemptedFlowIdRef.current === flow.flowId) return
    recoveryAttemptedFlowIdRef.current = flow.flowId
    void completeFlowMutation
      .mutateAsync({
        vendorId: vendor.id,
        flowId: flow.flowId,
      })
      .then((result) => {
        if (!isOpenRef.current) return
        if (result.status === 'CONFIRMED') {
          clearRecoverableVendor(organizationId, vendor.id)
          clearVendorSession()
          onClose()
          return
        }
        setCreatedVendor(vendor)
        if (result.status === 'AWAITING_ACKNOWLEDGEMENT') {
          handleCollectionSubmitted(flow)
        } else {
          setSubmittedFlow(null)
        }
      })
      .catch(() => {
        if (!isOpenRef.current) return
        setCreatedVendor(vendor)
        setSubmittedFlow(null)
      })
  }, [
    clearVendorSession,
    completeFlowMutation,
    createdVendor,
    handleCollectionSubmitted,
    isOpen,
    onClose,
    organizationId,
    recoveryVendorQuery.data,
  ])

  useEffect(() => {
    if (step !== 'payment' || !isOpen) return
    if (paymentStepPhase === 'collecting') collectRegionRef.current?.focus()
    else confirmationRegionRef.current?.focus()
  }, [paymentStepPhase, step, isOpen])

  // Closing never discards a Vendor that Step 1 already persisted. Reopening
  // resumes its collection flow, or its submitted flow at an unchecked
  // acknowledgement. Confirmation timers and consent are always reset.
  useEffect(() => {
    if (isOpen) return
    setAcknowledged(false)
    const requiresFreshFlow =
      hasUnsupportedAcknowledgementCopy ||
      confirmation.errorKind === 'stale-or-terminal'
    resetConfirmation()
    setHasUnsupportedAcknowledgementCopy(false)

    if (createdVendor) {
      setStep('payment')
      setSubmitError(null)
      if (requiresFreshFlow) setSubmittedFlow(null)
      return
    }

    // Keep an in-flight create intent intact so a late success can retain the
    // persisted Vendor and reopen directly into Step 2.
    if (createVendor.isPending) return
    clearVendorSession()
  }, [
    isOpen,
    createdVendor,
    createVendor.isPending,
    hasUnsupportedAcknowledgementCopy,
    confirmation.errorKind,
    resetConfirmation,
    clearVendorSession,
  ])

  const emailInvalid = email.trim() !== '' && !isValidEmailFormat(email)
  const phoneInvalid =
    phone.trim() !== '' &&
    !isValidPhoneNumber(phone, phoneIsoCountry as CountryCode)

  // canSave === the prototype's `canSave`: Vendor Name present, Vendor Email
  // present AND format-valid, phone valid-if-non-empty. Vendor ID / Mailing
  // Address / Contact Name / Description are all optional — Vendor ID is
  // explicitly NOT required (regression guard: it used to be).
  const canSave =
    name.trim() !== '' &&
    email.trim() !== '' &&
    isValidEmailFormat(email) &&
    !phoneInvalid

  const handleConfirmAndAdd = useCallback((): void => {
    const vendorId = createdVendor?.id
    const flowId = submittedFlow?.flowId
    const acknowledgementCopyVersion = submittedFlow?.acknowledgementCopyVersion
    if (
      !vendorId ||
      !flowId ||
      !acknowledged ||
      paymentStepPhase !== 'acknowledgement' ||
      acknowledgementCopyVersion !== VENDOR_ACKNOWLEDGEMENT.version ||
      confirmation.isInFlight
    ) {
      return
    }
    const input: ConfirmVendorAccountCollectionFlowInput = {
      vendorId,
      flowId,
      acknowledged: true,
      acknowledgementCopyVersion,
    }
    confirmation.startConfirmation(input)
  }, [
    createdVendor,
    submittedFlow,
    acknowledged,
    paymentStepPhase,
    confirmation.isInFlight,
    confirmation.startConfirmation,
  ])

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault()
    setSubmitError(null)
    if (name.trim() === '') setNameErrorArmed(true)
    if (email.trim() === '') setEmailErrorKind('required')
    else if (emailInvalid) setEmailErrorKind('invalid')
    if (phoneInvalid) setPhoneErrorArmed(true)
    if (!canSave || createVendor.isPending) return
    const request: CreateVendorRequest = {
      name: name.trim(),
      email: email.trim(),
      contactName: trimmedOrUndefined(contactName),
      phone:
        phone.trim() === ''
          ? undefined
          : `+${getCountryCallingCode(phoneIsoCountry as CountryCode)}${phone.trim()}`,
      externalId: trimmedOrUndefined(externalId),
      notes: trimmedOrUndefined(notes),
    }
    const fingerprint = JSON.stringify(request)
    if (createIdempotencyRef.current?.fingerprint !== fingerprint) {
      createIdempotencyRef.current = {
        fingerprint,
        key: createVendorIdempotencyKey(),
      }
    }
    const idempotencyKey = createIdempotencyRef.current.key
    // Keep the create failure on the mutate callback so a failed save surfaces
    // inline and the user stays on Step 1 with fields intact (the flyout does
    // not unmount on error).
    createVendor.mutate(
      { request, idempotencyKey },
      {
        onError: () => {
          if (
            !isOpenRef.current ||
            createIdempotencyRef.current?.key !== idempotencyKey
          ) {
            return
          }
          setSubmitError(
            translate(
              'Something went wrong adding this vendor. Please try again.',
            ),
          )
        },
      },
    )
  }

  const confirmationErrorMessage = hasUnsupportedAcknowledgementCopy
    ? translate(
        'The acknowledgement text for this bank account is no longer supported. Close this window and start again.',
      )
    : confirmation.errorKind === 'stale-or-terminal'
      ? translate(
          'This bank account setup can no longer be confirmed. Close this window and start again.',
        )
      : confirmation.errorKind === 'not-found'
        ? translate(
            'This vendor or bank account setup could not be found. Please try again or close this window and start again.',
          )
        : confirmation.errorKind === 'acknowledgement-required'
          ? translate(
              'We could not record your acknowledgement. Please try again.',
            )
          : confirmation.errorKind === 'provider'
            ? translate(
                'We could not confirm this bank account right now. Please try again.',
              )
            : confirmation.errorKind === 'unexpected'
              ? translate(
                  'We received an unexpected confirmation response. Please try again.',
                )
              : null

  const title =
    step === 'details'
      ? translate('Add Vendor')
      : translate('Add ACH Payment Details')
  const stepDescription =
    step === 'details'
      ? translate('Create a vendor with the details needed to pay them.')
      : translate(
          "Enter this vendor's bank account details below. After submitting, review and confirm your acknowledgement.",
        )

  return (
    <BaseFlyout
      className={cn('AddVendorFlyout', className)}
      css={style}
      isOpen={isOpen}
      onClose={onClose}
      aria-label={translate('Add Vendor')}
    >
      <div css={header}>
        <h2 css={flyoutTitle}>{title}</h2>
        <p css={description}>{stepDescription}</p>
      </div>

      {step === 'details' ? (
        <form css={form} onSubmit={handleSubmit} noValidate>
          <div css={fields}>
            <TextInput
              label={translate('Vendor Name')}
              requirement={'required'}
              placeholder={translate('Enter vendor name')}
              value={name}
              onChange={(event) => setName(event.target.value)}
              errorText={
                nameErrorArmed && name.trim() === ''
                  ? translate('Vendor name is required')
                  : undefined
              }
              testId={'add-vendor-name'}
            />
            <TextInput
              label={translate('Vendor Email')}
              requirement={'required'}
              type={'email'}
              inputMode={'email'}
              placeholder={translate('Enter vendor email')}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              errorText={
                emailErrorKind === 'required' && email.trim() === ''
                  ? translate('Vendor email is required')
                  : emailErrorKind === 'invalid' && emailInvalid
                    ? translate('Enter a valid email address')
                    : undefined
              }
              tooltipProps={{
                children: translate(
                  'Payment remittance details are sent to this address every time you pay this vendor.',
                ),
              }}
              testId={'add-vendor-email'}
            />
            <TextInput
              label={translate('Vendor ID')}
              placeholder={translate('Enter vendor ID')}
              value={externalId}
              onChange={(event) => setExternalId(event.target.value)}
              tooltipProps={{
                children: translate(
                  "Your organization's own identifier for this vendor, like an ID from your accounting system.",
                ),
              }}
              testId={'add-vendor-external-id'}
            />
            <div css={contactNameLabelOrder}>
              <TextInput
                label={translate('Contact Name')}
                requirement={'optional'}
                placeholder={translate('Enter contact name')}
                value={contactName}
                onChange={(event) => setContactName(event.target.value)}
                tooltipProps={{
                  children: translate(
                    'Your accounts payable contact at this vendor — who to reach for billing questions.',
                  ),
                }}
                testId={'add-vendor-contact-name'}
              />
            </div>
            <div css={phoneField}>
              <FieldLabel htmlFor={'vendorPhoneNumber'}>
                {translate('Phone Number')}{' '}
                <span>{translate('(Optional)')}</span>
              </FieldLabel>
              <div css={phoneRow}>
                <CountryPhoneDropdown
                  id={'vendor'}
                  selectedValue={phoneIsoCountry}
                  chooseOption={setPhoneIsoCountry}
                />
                <PhoneTextInputField
                  id={'vendor'}
                  css={phoneInput}
                  countryIso={phoneIsoCountry}
                  value={phone}
                  onChange={setPhone}
                  errors={
                    phoneErrorArmed && phoneInvalid
                      ? [translate('Enter a valid phone number')]
                      : undefined
                  }
                />
              </div>
            </div>
            <TextAreaInput
              aria-label={translate('Vendor Description')}
              placeholder={translate('Enter vendor description')}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={3}
              testId={'add-vendor-notes'}
            />
          </div>
          <div css={footer}>
            {submitError && (
              <p css={footerError} role={'alert'}>
                {submitError}
              </p>
            )}
            <div css={footerButtons}>
              {/* inlineMuted in the prototype -> ext-ui `no-style` variant. */}
              <Button
                variant={'no-style'}
                type={'button'}
                onClick={onClose}
                data-testid={'add-vendor-cancel'}
              >
                {translate('Cancel')}
              </Button>
              <Button
                variant={'primary'}
                type={'submit'}
                disabled={createVendor.isPending}
                loading={createVendor.isPending}
                data-testid={'add-vendor-submit'}
              >
                {translate('Save and Continue')}
              </Button>
            </div>
          </div>
        </form>
      ) : !createdVendor ? (
        <div css={fields} ref={confirmationRegionRef} tabIndex={-1}>
          <p
            css={recoveryVendorQuery.isError ? footerError : pendingNote}
            role={recoveryVendorQuery.isError ? 'alert' : 'status'}
            aria-live={recoveryVendorQuery.isError ? undefined : 'polite'}
          >
            {recoveryVendorQuery.isError
              ? translate(
                  'We could not restore this vendor bank account setup. Close this window and try again.',
                )
              : translate('Restoring this vendor bank account setup…')}
          </p>
        </div>
      ) : paymentStepPhase === 'collecting' ? (
          // Phase A — collecting. The MT embed lives inside the flyout body,
          // wrapped in the dashed trust-boundary treatment. No flyout footer:
          // the embed owns its own Submit.
          <div css={fields} ref={collectRegionRef} tabIndex={-1}>
            <div css={trustBox}>
              <MtAccountCollection
                createFlow={createFlowForEmbed}
                completeFlow={completeFlowForEmbed}
                onLinked={handleCollectionLinked}
              />
            </div>
          </div>
        ) : (
          // Phase B — the provider accepted the hosted form. Only the user's
          // later checkbox + explicit button activation can start /confirm.
          <div
            css={confirmationSurface}
            ref={confirmationRegionRef}
            tabIndex={-1}
          >
            <div css={fields}>
              {paymentStepPhase === 'confirmation-pending' && (
                <p css={pendingNote} role={'status'} aria-live={'polite'}>
                  {confirmation.canRetry
                    ? translate(
                        'This bank account is still being confirmed. Try again to check its status.',
                      )
                    : translate(
                        'This bank account is still being confirmed. This can take a moment.',
                      )}
                </p>
              )}
              {paymentStepPhase === 'confirmation-error' &&
                confirmationErrorMessage && (
                  <p css={footerError} role={'alert'}>
                    {confirmationErrorMessage}
                  </p>
                )}
              {paymentStepPhase === 'confirming' && (
                <p css={pendingNote} role={'status'} aria-live={'polite'}>
                  {translate('Confirming this bank account…')}
                </p>
              )}
            </div>
            <div css={footer}>
              {!hasUnsupportedAcknowledgementCopy && submittedFlow ? (
                <div css={ackBlock}>
                  <div css={ackRow}>
                    <Checkbox
                      id={'vendor-ack'}
                      checked={acknowledged}
                      disabled={paymentStepPhase !== 'acknowledgement'}
                      onChange={(event) =>
                        setAcknowledged(event.target.checked)
                      }
                      aria-label={translate(
                        'I acknowledge these ACH account details are accurate and authorized for use when paying this vendor.',
                      )}
                      data-testid={'vendor-ack-checkbox'}
                    />
                    <label css={ackText} htmlFor={'vendor-ack'}>
                      <span css={ackPrimary}>
                        {translate('I acknowledge that...')}
                      </span>
                      <span css={ackDescription}>
                        {translate(
                          'These ACH account details are accurate and authorized for use when paying this vendor.',
                        )}
                      </span>
                    </label>
                  </div>
                  <div css={footerActionsEnd}>
                    {confirmation.canRetry ? (
                      <Button
                        variant={'secondary'}
                        onClick={confirmation.retry}
                      >
                        {translate('Try again')}
                      </Button>
                    ) : confirmation.errorKind !== 'stale-or-terminal' ? (
                      <Button
                        variant={'primary'}
                        disabled={
                          paymentStepPhase !== 'acknowledgement' ||
                          !acknowledged ||
                          confirmation.isInFlight
                        }
                        loading={confirmation.isInFlight}
                        onClick={handleConfirmAndAdd}
                        data-testid={'vendor-ack-confirm'}
                      >
                        {translate('Confirm and Add')}
                      </Button>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        )}
    </BaseFlyout>
  )
}

export default AddVendorFlyout
